import React from 'react';
import useAutoFocusFirstField from 'hooks/useAutoFocusFirstField';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import { Button, FormControlLabel, Switch, TextField } from '@material-ui/core';

import ConfirmDialog from 'components/ConfirmDialog';
import { getSimpleVenuesWithStockModuleActive } from 'services/venuesServices';
import Venue from 'types/models/Venue';
import { ProductionCenterFormSchema } from 'forms/schemas/StockModuleForm';
import { ProductionCenterType, StockProductionCenter } from 'types/models/StockReceipt';
import { storeStockProductionCenter, updateStockProductionCenter } from 'services/stockProductionCenterServices';
import SelectField from 'components/SelectField';
import { useTranslation } from 'react-i18next';
import { mapToSelectOptions } from 'utils/select';
import { getApiErrorMessage } from 'utils/errors';
import SaveError from 'components/common/SaveError';
import AppContext from 'contexts/AppContext';
import useSetupInitialState from 'hooks/useSetupInitialState';
import UserRoleType from 'types/enums/UserRoleType';

type Props = {
  productionCenter?: StockProductionCenter;
};

export default ({ productionCenter }: Props): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();

  const { authData } = React.useContext(AppContext);
  const entity = useSetupInitialState();
  const isVenueEditor = authData?.user?.role === UserRoleType.VenueEditor;

  const [venues, setVenues] = React.useState<Venue[]>([]);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const formRef = useAutoFocusFirstField(true);

  React.useEffect(() => {
    if (isVenueEditor) return;

    (async () => {
      try {
        const data = await getSimpleVenuesWithStockModuleActive();
        setVenues(data ?? []);
      } catch {}
    })();
  }, [isVenueEditor]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      venueId: productionCenter?.venueId ?? entity?.id ?? '',
      name: productionCenter?.name ?? '',
      type: productionCenter?.type ?? ProductionCenterType.INTERNAL,
      isActive: productionCenter?.isActive ?? true,
    },
    validationSchema: () => ProductionCenterFormSchema(t),
    onSubmit: () => setOpenDialog(true),
  });

  async function onConfirm() {
    setSaveError(null);
    const payload = {
      venueId: formik.values.venueId,
      name: formik.values.name,
      type: formik.values.type,
      isActive: formik.values.isActive,
    };

    try {
      if (productionCenter) {
        await updateStockProductionCenter(productionCenter.id, payload);
      } else {
        await storeStockProductionCenter(payload);
      }
      history.go(-1);
    } catch (error) {
      setSaveError(getApiErrorMessage(error) ?? t('errors.generic'));
    } finally {
      formik.setSubmitting(false);
    }
  }

  return (
    <>
      <ConfirmDialog
        open={openDialog}
        setOpen={setOpenDialog}
        onConfirm={onConfirm}
        onCancel={() => formik.setSubmitting(false)}
      />

      <form onSubmit={formik.handleSubmit} ref={formRef}>
        {!isVenueEditor && (
          <SelectField
            name="venueId"
            label={t('stock.production-centers.form.venue')}
            placeholder={t('stock.production-centers.form.venue-placeholder')}
            value={formik.values.venueId}
            options={mapToSelectOptions(venues, { label: 'name' })}
            isMulti={false}
            setFieldValue={formik.setFieldValue}
            touched={formik.touched.venueId}
            error={formik.errors.venueId}
            isClearable={false}
            backspaceRemovesValue={false}
            isDisabled={formik.isSubmitting}
            isSearchable
            onBlur={formik.handleBlur}
          />
        )}

        <TextField
          fullWidth
          margin="normal"
          label={t('stock.production-centers.form.name')}
          name="name"
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
          disabled={formik.isSubmitting}
        />

        <SelectField
          name="type"
          label={t('stock.production-centers.form.type')}
          placeholder={t('stock.production-centers.form.type-placeholder')}
          value={formik.values.type}
          options={Object.values(ProductionCenterType).map((type) => ({
            label: t(`stock.production-centers.types.${type}`),
            value: type,
          }))}
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          touched={formik.touched.type}
          error={formik.errors.type}
          isClearable={false}
          backspaceRemovesValue={false}
          isDisabled={formik.isSubmitting}
          isSearchable={false}
          onBlur={formik.handleBlur}
        />

        <FormControlLabel
          control={
            <Switch
              checked={formik.values.isActive}
              onChange={(e) => formik.setFieldValue('isActive', e.target.checked)}
            />
          }
          label={t('stock.production-centers.form.active')}
        />

        <SaveError error={saveError} />

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 16,
            marginTop: 24,
          }}
        >
          <Button color="primary" variant="contained" type="submit" disabled={formik.isSubmitting}>
            {productionCenter
              ? t('stock.production-centers.form.buttons.save')
              : t('stock.production-centers.form.buttons.create')}
          </Button>
        </div>
      </form>
    </>
  );
};
